import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAppSelector } from '../../../shared/hooks/use-app-selector';
import { Navigate } from 'react-router-dom';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import createQueryConfig from '../../../queries';
import useMerchantSettings from '../../../hooks/use-merchant-settings';
import { isResellerAccount, isWhitelabelAccount } from '../../../shared/utils';
import type {
  CheckoutUrlPayload,
  CheckoutUrlResponse,
  Subscription,
  SubscriptionMfeProps,
} from '../types';

const CHECKOUT_SUCCESS_REDIRECT_URL = `${location.origin}/success-checkout`;

const iframeStyle = {
  flex: 1,
  height: '100%',
  border: 'unset',
};

export function Upgrade({ merchantId, locale, token }: SubscriptionMfeProps) {
  const [isLoading, toggleLoading] = useState(true);
  const [checkoutUrlResponse, setCheckoutUrlResponse] =
    useState<CheckoutUrlResponse | null>(null);
  const { apiClient } = useGlobals();

  function createUpgradeCheckoutSession() {
    return apiClient.subscriptionV3.createCheckoutSession(
      {
        merchantId,
        locale,
        operation: 'upgrade',
        productFamily: 'shore_bm',
        redirectUrl: CHECKOUT_SUCCESS_REDIRECT_URL,
        metadata: {
          app: 'calendar-web',
          channel: 'self-service',
        },
      },
      token
    );
  }

  function handleUpgradeCheckoutUrlResponse(response: CheckoutUrlResponse) {
    setCheckoutUrlResponse(response);
    toggleLoading(false);
  }

  const { mutate: createUpgradeCheckoutLink } = useMutation({
    mutationFn: createUpgradeCheckoutSession,
    retry: 3,
    retryDelay: 3000,
    onSuccess: (res) => {
      res
        .json()
        .then(({ checkout_url: checkoutUrl }: CheckoutUrlPayload) => {
          handleUpgradeCheckoutUrlResponse([false, checkoutUrl]);
        })
        .catch(() => {
          handleUpgradeCheckoutUrlResponse([true, null]);
        });
    },
    onError: (error) => {
      console.error('Failed to create upgrade checkout session: ', error);

      handleUpgradeCheckoutUrlResponse([true, null]);
    },
  });

  useEffect(() => {
    createUpgradeCheckoutLink();
  }, [createUpgradeCheckoutLink]);

  if (isLoading) {
    return null;
  }

  const [error, checkoutUrl] = checkoutUrlResponse ?? [false, null];

  if (error) {
    return <p>Failed to load subscription checkout</p>;
  }

  return (
    <iframe
      title="Shore's subscription checkout"
      style={iframeStyle}
      src={checkoutUrl ?? undefined}
    />
  );
}

export function SubscriptionUpgrade() {
  const currentMerchant = useAppSelector((state) => state.currentMerchant);
  const { isLoading: isLoadingMerchantSettings, merchantSettings } =
    useMerchantSettings();
  const { queryClient, apiClient } = useGlobals();
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const { data: token, status: tokenQueryStatus } = useQuery(
    queryConfig.authToken()
  );
  const { status: subscriptionQueryStatus, data: subscription } =
    useQuery<Subscription>(queryConfig.subscription(merchantSettings));

  const isLoading =
    isLoadingMerchantSettings ||
    [subscriptionQueryStatus, tokenQueryStatus].includes('pending');

  if (isLoading) {
    return null;
  }

  if (tokenQueryStatus === 'error') {
    return <p>Session expired</p>;
  }

  if (subscriptionQueryStatus === 'error') {
    return (
      <p>Failed to get subscription information. Please try again later.</p>
    );
  }

  const { merchantId, locale } = merchantSettings;
  const subscriptionPartner = subscription?.customer?.partner;

  const isWhitelabel = isWhitelabelAccount(currentMerchant.whiteLabel);
  const isReseller = isResellerAccount(subscriptionPartner ?? null);

  if (isWhitelabel || isReseller) {
    return <Navigate to="/" />;
  }

  return <Upgrade merchantId={merchantId} locale={locale} token={token} />;
}
